import { useCallback, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import cross from '~/assets/icons/cross.svg';
import { playSound, Sound } from '~/assets/sounds';
import { PanelControls } from '~/components/ui/PanelControls';
import { ShopContainer } from '~/components/ui/Shop';
import { ShopBalance } from '~/components/ui/ShopBalance';
import { ShopHead } from '~/components/ui/ShopHead';
import { ShopPreview } from '~/components/ui/ShopPreview';
import { rage, useInputAction, useMouseButton, useObservable } from '~/rage';
import {
	InputAction,
	InputKey,
	KeyPressStates,
	MouseButton,
	WalletType,
} from '~/shared';
import logo from '../../assets/images/logo.svg';
import {
	CategoryTab,
	CategoryTypes,
	getCategoryByType,
} from '../../components/CategoryTab';
import { ListTab } from '../../components/ListTab';
import { PaintTab } from '../../components/PaintTab';
import { useBuy, useShopPreview, useVehiclePaintLoader } from '../../hooks';
import {
	resetStore,
	setActiveStage,
	setInterfaceHidden,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledCloseButtonContainer,
	StyledContainer,
	StyledControlsContainer,
	StyledRightContainer,
} from './CarPaint.styled';

const CarPaint: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const activeStage = useLayoutSelector((state) => state.carPaint.activeStage);
	const activeCategory = useLayoutSelector(
		(state) => state.carPaint.activeCategory,
	);
	const { handleBuy } = useBuy();
	const { loadVehiclePaint } = useVehiclePaintLoader();
	const shopPreview = useShopPreview();
	const interfaceHidden = useLayoutSelector(
		(state) => state.carPaint.interfaceHidden,
	);
	const modalState = useObservable(rage.modal.getCurrentModal());

	const handleMouseButton = useCallback((state: KeyPressStates) => {
		if (state === KeyPressStates.Pressed) {
			dispatch(setInterfaceHidden(true));
		} else if (state === KeyPressStates.Unpressed) {
			dispatch(setInterfaceHidden(false));
		}
	}, []);

	const leftControls = useMemo(() => {
		const category = getCategoryByType(activeCategory);

		const defaultButtons = [
			{
				text: 'Крутить транспорт',
				keys: [InputKey.MouseRight],
			},
		];

		if (activeStage === 'category') {
			return [
				{
					text: 'Управление',
					keys: [
						InputKey.ArrowUp,
						InputKey.ArrowDown,
						InputKey.ArrowLeft,
						InputKey.ArrowRight,
					],
				},
				{
					text: 'Выбрать категорию',
					keys: [InputKey.Enter],
					onClick: () =>
						dispatch(
							setActiveStage(
								category?.type === CategoryTypes.PaintingWheels
									? 'wheel'
									: 'list',
							),
						),
				},
				...defaultButtons,
			];
		}

		if (activeStage === 'list') {
			return [
				{
					text: 'Управление',
					keys: [InputKey.ArrowUp, InputKey.ArrowDown],
				},
				{
					text: 'Купить',
					keys: [InputKey.Enter],
					onClick: () => handleBuy(WalletType.Cash),
				},
				...defaultButtons,
			];
		}

		if (activeStage === 'paint') {
			return [
				{
					text: 'Купить',
					keys: [InputKey.Enter],
					onClick: () => handleBuy(WalletType.Cash),
				},
				...defaultButtons,
			];
		}

		return [];
	}, [activeStage, activeCategory, handleBuy]);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => {
				rage.vehiclePaintShop
					.exit()
					.catch((err) => rage.notifications.showException(err));
			},
		},
	];

	useInputAction(InputAction.CommonMenuClose, () => {
		if (modalState || interfaceHidden) {
			return;
		}

		if (activeStage === 'list' || activeStage === 'paint') {
			dispatch(setActiveStage('category'));
			return;
		}

		rage.vehiclePaintShop
			.exit()
			.catch((err) => rage.notifications.showException(err));
	});

	useMouseButton(MouseButton.Right, handleMouseButton, {
		state: KeyPressStates.Both,
	});

	useEffectOnce(() => {
		loadVehiclePaint().catch((err) => rage.notifications.showException(err));

		return () => {
			dispatch(resetStore());
		};
	});

	return (
		<StyledContainer onContextMenu={(e) => e.preventDefault()}>
			<StyledRightContainer style={{ opacity: interfaceHidden ? 0 : 1 }}>
				<ShopBalance />
				<StyledCloseButtonContainer
					onClick={() => {
						rage.vehiclePaintShop.exit();
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<img src={cross} width={20} height={20} />
				</StyledCloseButtonContainer>
			</StyledRightContainer>
			<ShopContainer style={{ opacity: interfaceHidden ? 0 : 1 }}>
				<ShopHead img={logo} text={'Тюнинг студия'} height={30} />
				{activeStage === 'list' && <ListTab />}
				{activeStage === 'category' && <CategoryTab />}
				{activeStage === 'paint' && <PaintTab />}
			</ShopContainer>
			<StyledControlsContainer style={{ opacity: interfaceHidden ? 0 : 1 }}>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
			<ShopPreview
				{...shopPreview}
				visible={shopPreview.visible && !interfaceHidden}
			/>
		</StyledContainer>
	);
};

export { CarPaint };
